import React, { useState } from 'react';
import { Button, InputAdornment, MenuItem, styled, TextField } from '@mui/material';
import { format } from 'date-fns';
import MoneyInput from 'components/masked-input/MoneyInput';
import { moneyFormat } from 'helpers/moneyFormat';
import { NewBudgetPayment } from 'types/budgetPayment';
import { PaymentMethod } from 'types/paymentMethod';
import { asInputComponent } from 'components/masked-input/asInputComponent';

const Row = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr auto',
  gap: theme.spacing(1.5),
  alignItems: 'start',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr 1fr',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

interface BudgetPaymentFormProps {
  balance: number;
  methods: PaymentMethod[];
  saving: boolean;
  onSubmit(payment: NewBudgetPayment): Promise<boolean>;
}

const BudgetPaymentForm: React.FC<BudgetPaymentFormProps> = ({ balance, methods, saving, onSubmit }) => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const [value, setValue] = useState(0);
  const [paidAt, setPaidAt] = useState(today);
  const [methodId, setMethodId] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const valueError =
    value <= 0 ? 'Informe o valor' : value > balance + 0.001 ? `Máximo ${moneyFormat(balance)} (saldo)` : '';
  const dateError = !paidAt ? 'Informe a data' : paidAt > today ? 'A data não pode ser futura' : '';
  const methodError = methodId ? '' : 'Informe a forma de pagamento';
  const invalid = !!(valueError || dateError || methodError);

  function handleSubmit() {
    setSubmitted(true);
    if (invalid) return;

    onSubmit({ value, paid_at: paidAt, payment_method_id: Number(methodId) }).then(ok => {
      if (!ok) return;

      setValue(0);
      setSubmitted(false);
    });
  }

  return (
    <Row>
      <TextField
        label="Valor"
        size="small"
        value={value}
        onChange={e => setValue(Number(e.target.value))}
        error={submitted && !!valueError}
        helperText={submitted ? valueError : `Saldo ${moneyFormat(balance)}`}
        slotProps={{
          input: {
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            inputComponent: asInputComponent(MoneyInput),
          },
        }}
      />

      <TextField
        label="Data do pagamento"
        size="small"
        type="date"
        value={paidAt}
        onChange={e => setPaidAt(e.target.value)}
        error={submitted && !!dateError}
        helperText={submitted ? dateError : ' '}
        slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
      />

      <TextField
        select
        label="Forma de pagamento"
        size="small"
        value={methodId}
        onChange={e => setMethodId(e.target.value)}
        error={submitted && !!methodError}
        helperText={submitted ? methodError : ' '}
      >
        {methods.map(method => (
          <MenuItem key={method.id} value={String(method.id)}>
            {method.name}
          </MenuItem>
        ))}
      </TextField>

      <Button variant="contained" disabled={saving} onClick={handleSubmit} sx={{ height: 40 }}>
        Informar
      </Button>
    </Row>
  );
};

export default BudgetPaymentForm;
